import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Mail, Sparkles } from "lucide-react";
import Aurora from "@/components/Aurora";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login({ email: email.trim().toLowerCase(), password });
      navigate("/dashboard", { replace: true });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden grid place-items-center px-5 py-16">
      <Aurora variant="intense" rings />
      <div className="relative max-w-md w-full">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <span className="inline-flex w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-600 grid place-items-center shadow-[0_0_24px_rgba(0,229,255,0.5)]">
            <Sparkles className="w-4 h-4 text-black" strokeWidth={2.5} />
          </span>
          <span className="font-display text-white text-lg">
            <span className="font-normal">Favor</span>{" "}
            <span className="font-bold">Studio.</span>
          </span>
        </Link>

        <div className="glass-card p-7 sm:p-9">
          <div className="label-chip"><span className="dot" /><span>STUDENT LOGIN</span></div>
          <h1 className="font-display text-3xl sm:text-4xl mt-5">
            <span className="font-normal">Welcome </span>
            <span className="font-bold">Back.</span>
          </h1>
          <p className="mt-3 text-zinc-400 text-sm leading-relaxed">
            Log in to access your AI Agents course.
          </p>

          <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="login-form">
            <div>
              <label className="text-[11px] tracking-[0.25em] uppercase text-zinc-500">Email</label>
              <div className="mt-1.5 relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-transparent border border-white/10 focus:border-cyan-400/60 focus:outline-none rounded-full pl-11 pr-5 py-3 text-white placeholder:text-zinc-500 text-sm transition-colors"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] tracking-[0.25em] uppercase text-zinc-500">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full bg-transparent border border-white/10 focus:border-cyan-400/60 focus:outline-none rounded-full px-5 py-3 text-white placeholder:text-zinc-500 text-sm transition-colors"
              />
            </div>
            {err && (
              <p data-testid="login-error-message" className="text-rose-300 text-sm">{err}</p>
            )}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={busy}
              className="btn-aurora w-full disabled:opacity-70"
            >
              {busy ? (<><Loader2 className="w-4 h-4 animate-spin" /> LOGGING IN…</>) : (<>LOG IN <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-zinc-500 mt-6">
          Don&apos;t have access yet? <Link to="/" className="text-cyan-300 hover:underline">Enroll for $997</Link>
        </p>
      </div>
    </div>
  );
}
